import { useRef } from "react";
import { makeStyles } from "tss-react/mui";
import { ICONS_WEB } from "@miplatform/constants";
import { getInitials } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { IconButton } from "@mui/material";
import { MiAvatar } from "../MiAvatar/MiAvatar";
import { MiIcon } from "../MiIcon/MiIcon";

/**
 * A profile picture component that displays an avatar with optional edit and delete functionality.
 *
 * @param {Object} props - The component props
 * @param {boolean} [props.disabled=false] - Whether the component is disabled
 * @param {boolean} [props.isEditDisabled=disabled] - Whether the edit functionality is disabled
 * @param {boolean} [props.isDeleteDisabled=disabled] - Whether the delete functionality is disabled
 * @param {string} [props.src] - The source URL for the profile picture image
 * @param {number} [props.size=130] - The size of the profile picture in pixels
 * @param {number} [props.fontSize] - Custom font size for initials or code display
 * @param {string} [props.code] - A code/abbreviation to display when no image is provided
 * @param {string} [props.name] - The name to generate initials from when no image or code is provided
 * @param {Function} [props.onEdit] - Callback function called when editing the profile picture with the selected file
 * @param {Function} [props.onDelete] - Callback function called when deleting the profile picture
 * @param {Object} [props.sx={}] - Additional styling object passed to the MiAvatar component
 *
 * @returns {JSX.Element} A profile picture component with optional edit/delete controls
 */
export const MiProfilePicture = ({
  disabled = false,
  isEditDisabled = disabled,
  isDeleteDisabled = disabled,
  src,
  size = 130,
  fontSize,
  code,
  name,
  onEdit,
  onDelete,
  sx = {},
}) => {
  const profileInputRef = useRef();
  const { classes, theme } = useStyles({ size });

  const getRenderedAvatar = () => {
    if (src) {
      return null;
    }
    if (code) {
      return code;
    }
    if (name) {
      return getInitials(name)?.toUpperCase();
    }

    return (
      <MiIcon
        color="white"
        path={mdi[ICONS_WEB.ACCOUNT]}
        size="100%"
        className={classes.icon}
      />
    );
  };

  const renderAvatar = () => (
    <MiAvatar
      {...(src && {
        src,
        alt: "Profile Picture",
      })}
      sx={{
        width: "100%",
        height: "100%",
        fontSize: fontSize ?? size / 4,
        ...sx,
      }}
    >
      {getRenderedAvatar()}
    </MiAvatar>
  );

  return (
    <div className={classes.imgContainer}>
      {onEdit && (
        <IconButton
          size="small"
          data-testid="profile-picture-edit-btn"
          disabled={isEditDisabled}
          className={classes.editButton}
          onClick={() => profileInputRef.current.click()}
        >
          <MiIcon path={mdi[ICONS_WEB.PENCIL]} size={0.75} />
        </IconButton>
      )}
      {src !== undefined && onDelete && (
        <IconButton
          size="small"
          data-testid="profile-picture-delete-btn"
          disabled={isDeleteDisabled}
          className={classes.deleteButton}
          onClick={onDelete}
        >
          <MiIcon
            path={mdi[ICONS_WEB.TRASH_CAN]}
            size={0.75}
            color={theme.palette.red.dark}
          />
        </IconButton>
      )}
      <input
        ref={profileInputRef}
        type="file"
        data-testid="profile-picture-input"
        accept="image/*"
        onChange={async (event) => {
          const file = event.target.files[0];
          if (file) {
            await onEdit(file);
          }
          profileInputRef.current.value = "";
        }}
        style={{ display: "none" }}
      />
      {renderAvatar()}
    </div>
  );
};

const useStyles = makeStyles()((theme, { size }) => ({
  imgStyle: {
    height: "100%",
    width: "100%",
    borderRadius: "50%",
  },
  editButton: {
    position: "absolute",
    bottom: 0,
    right: 0,
    zIndex: 2,
    backgroundColor: "rgba(0,0,0,0.5)",
    color: theme.palette.common.white,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.35)",
    },
    "&:disabled": {
      backgroundColor: "rgba(0,0,0,0.15)",
      color: theme.palette.common.white,
    },
  },
  deleteButton: {
    position: "absolute",
    top: 0,
    right: 0,
    zIndex: 2,
    "&:disabled": {
      color: theme.palette.red.accent,
    },
  },
  imgContainer: {
    height: size,
    width: size,
    borderRadius: "50%",
    position: "relative",
  },
  icon: {
    backgroundColor: theme.palette.grey.accent,
    borderRadius: "50%",
    color: "white",
  },
}));
